import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import type { TAttribute } from "../schemas";

const FALLBACK_IMAGE = "https://via.placeholder.com/80x80?text=No+Image";

interface AttributeCardProps {
  attribute: TAttribute;
  onEdit: (attribute: TAttribute) => void;
  onDelete: (attribute: TAttribute) => void;
}

const AttributeCard: React.FC<AttributeCardProps> = ({
  attribute,
  onEdit,
  onDelete,
}) => {
  return (
    <Box sx={styles.cardContainer}>
      {/* Left column - Image and Icon */}
      <Box sx={styles.leftColumn}>
        <Box sx={styles.imageContainer}>
          <img
            src={attribute.image || FALLBACK_IMAGE}
            alt={attribute.name}
            style={styles.image}
            onError={(e) => {
              (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
            }}
          />
        </Box>
        <Box sx={styles.iconContainer}>
          {attribute.icon ? (
            <img
              src={attribute.icon}
              alt={`${attribute.name} icon`}
              style={styles.iconImage}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <Typography sx={styles.iconPlaceholder}>Icon</Typography>
          )}
        </Box>
      </Box>

      {/* Middle column - Name and Description */}
      <Box sx={styles.middleColumn}>
        <Box sx={styles.nameContainer}>
          <Typography variant="h6" sx={styles.name}>
            {attribute.name}
          </Typography>
        </Box>
        <Box sx={styles.descriptionContainer}>
          <Typography variant="body2" sx={styles.description}>
            {attribute.description || "No description"}
          </Typography>
        </Box>
      </Box>

      {/* Right column - Edit and Delete buttons */}
      <Box sx={styles.rightColumn}>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => onEdit(attribute)}
            sx={styles.iconButton}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={() => onDelete(attribute)}
            sx={styles.iconButton}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default AttributeCard;

const styles = {
  cardContainer: {
    border: "1.5px solid #AAA",
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "#FFF",
    transition: "all 0.3s ease",
    display: "flex",
    flexDirection: "row",
    padding: 2,
    gap: 2,
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    flexShrink: 0,
  },
  imageContainer: {
    width: 80,
    height: 80,
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #ddd",
  },
  image: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain" as const,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #ddd",
  },
  iconImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain" as const,
  },
  iconPlaceholder: {
    fontFamily: "GilroyRegular",
    fontSize: 12,
    color: "#999",
  },
  middleColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
  },
  nameContainer: {
    borderRadius: 1,
    padding: "8px 12px",
    backgroundColor: "#f9f9f9",
    border: "1px solid #eee",
  },
  name: {
    fontFamily: "GilroyBold",
    fontSize: 16,
    wordBreak: "break-word",
  },
  descriptionContainer: {
    borderRadius: 1,
    padding: "8px 12px",
    backgroundColor: "#f9f9f5",
    border: "1px solid #eee",
    height: 120,
    overflowY: "auto",
  },
  description: {
    fontFamily: "GilroyRegular",
    fontSize: 14,
    color: "#666",
    wordBreak: "break-word",
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  iconButton: {
    border: "1px solid #ddd",
    borderRadius: 1,
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.05)",
    },
  },
};
